import React, { useRef, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, Trash2 } from 'lucide-react';
import { isCivilQuotationExclusion, moveQuotationExclusion, normalizeQuotationExclusionLabel, orderQuotationExclusions } from '@/constants/quotationExclusions';

export type QuotationExclusionOption = {
  QuotationExclusionOptionId?: number | null;
  Label: string;
  SortOrder?: number | null;
  IsActive?: boolean;
  IsSystem?: boolean;
  Source?: string;
};

type Props = {
  options: QuotationExclusionOption[];
  selected: string[];
  order: string[];
  onOrder: (labels: string[]) => void;
  onToggle: (label: string) => void;
  canDelete: (option: QuotationExclusionOption) => boolean;
  onDelete: (option: QuotationExclusionOption) => void;
  deletingLabel: string | null;
};

export default function QuotationExclusionEditor(props: Props) {
  const preview = useRef<HTMLDialogElement>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const labels = orderQuotationExclusions([...props.options.map((option) => option.Label), ...props.selected], props.order);
  const selected = new Set(props.selected.map((label) => normalizeQuotationExclusionLabel(label).toLowerCase()));
  const previewLabels = labels.filter((label) => selected.has(label.toLowerCase()));
  const move = (from: string, to: string, inPreview = false) => {
    const next = moveQuotationExclusion(labels, from, to, !inPreview);
    if (next !== labels) {
      props.onOrder(next);
      const group = next.filter((label) => inPreview ? selected.has(label.toLowerCase()) : isCivilQuotationExclusion(label) === isCivilQuotationExclusion(from));
      setAnnouncement(`${from} moved to position ${group.indexOf(from) + 1} of ${group.length}.`);
    }
  };
  return <>
    <div className="quotation-exclusion-toolbar">
      <p className="quotation-exclusion-help">Drag to reorder within each group, or use the arrows. Open the preview to arrange the full numbered PDF list.</p>
      <button type="button" className="quotation-exclusion-preview-button" onClick={() => preview.current?.showModal()}>Preview exclusions</button>
    </div>
    <dialog ref={preview} className="quotation-exclusion-preview" aria-labelledby="quotation-exclusion-preview-title">
      <div className="quotation-exclusion-toolbar">
        <h3 id="quotation-exclusion-preview-title">Exclusions preview</h3>
        <button type="button" className="quotation-exclusion-preview-button" onClick={() => preview.current?.close()}>Close preview</button>
      </div>
      <p>Drag rows or use the arrows to change the PDF order. Numbers update automatically, and changes apply to this draft immediately.</p>
      {previewLabels.length > 0
        ? <ol className="quotation-exclusion-preview-list">{previewLabels.map((label, index) => <li key={label} data-preview-exclusion={label}
          className={`quotation-exclusion-preview-row${over === label && dragged !== label ? ' quotation-exclusion-row--over' : ''}`}
          onDragOver={(event) => {
            if (!dragged || !selected.has(dragged.toLowerCase())) return;
            event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setOver(label);
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (dragged && selected.has(dragged.toLowerCase()) && event.dataTransfer.getData('application/x-pmapp-exclusion') === dragged) move(dragged, label, true);
            setDragged(null); setOver(null);
          }}>
          <button type="button" className="quotation-exclusion-control quotation-exclusion-grip" draggable
            aria-label={`Drag preview exclusion ${label}`} title="Drag to reorder"
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('application/x-pmapp-exclusion', label); setDragged(label);
            }} onDragEnd={() => { setDragged(null); setOver(null); }}><GripVertical size={16} aria-hidden="true" /></button>
          <span className="quotation-exclusion-preview-number" aria-hidden="true">{index + 1}.</span>
          <span className="quotation-exclusion-preview-text">{label}</span>
          <div className="quotation-exclusion-actions">
            <button type="button" className="quotation-exclusion-control" aria-label={`Move preview exclusion up: ${label}`} title="Move up" disabled={index === 0} onClick={() => move(label, previewLabels[index - 1], true)}><ArrowUp size={14} aria-hidden="true" /></button>
            <button type="button" className="quotation-exclusion-control" aria-label={`Move preview exclusion down: ${label}`} title="Move down" disabled={index === previewLabels.length - 1} onClick={() => move(label, previewLabels[index + 1], true)}><ArrowDown size={14} aria-hidden="true" /></button>
          </div>
        </li>)}</ol>
        : <p>No exclusions selected.</p>}
    </dialog>
    <div role="status" className="quotation-exclusion-status">{announcement}</div>
    <div className="quotation-exclusion-columns">
      {[false, true].map((civil) => {
        const group = labels.filter((label) => isCivilQuotationExclusion(label) === civil);
        return <section key={String(civil)} aria-label={civil ? 'Civil exclusions' : 'General and structural exclusions'}>
          <h4 className="quotation-exclusion-heading">{civil ? 'Civil' : 'General / Structural / Other'}</h4>
          <div className="quotation-exclusion-items">
            {group.map((label, index) => {
              const option = props.options.find((item) => normalizeQuotationExclusionLabel(item.Label).toLowerCase() === label.toLowerCase()) || { Label: label, Source: 'local' };
              return <div key={label} data-exclusion={label}
                className={`quotation-exclusion-row${over === label && dragged !== label ? ' quotation-exclusion-row--over' : ''}`}
                onDragOver={(event) => {
                  if (!dragged || isCivilQuotationExclusion(dragged) !== civil) return;
                  event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setOver(label);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragged && event.dataTransfer.getData('application/x-pmapp-exclusion') === dragged) move(dragged, label);
                  setDragged(null); setOver(null);
                }}>
                <button type="button" className="quotation-exclusion-control quotation-exclusion-grip" draggable
                  aria-label={`Drag exclusion ${label}`} title="Drag to reorder"
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('application/x-pmapp-exclusion', label); setDragged(label);
                  }} onDragEnd={() => { setDragged(null); setOver(null); }}>
                  <GripVertical size={16} aria-hidden="true" />
                </button>
                <label className="quotation-exclusion-label">
                  <input type="checkbox" checked={selected.has(label.toLowerCase())} onChange={() => props.onToggle(label)} />
                  <span>{label}</span>
                </label>
                <div className="quotation-exclusion-actions">
                  <button type="button" className="quotation-exclusion-control" aria-label={`Move exclusion up: ${label}`} title="Move up" disabled={index === 0} onClick={() => move(label, group[index - 1])}><ArrowUp size={14} aria-hidden="true" /></button>
                  <button type="button" className="quotation-exclusion-control" aria-label={`Move exclusion down: ${label}`} title="Move down" disabled={index === group.length - 1} onClick={() => move(label, group[index + 1])}><ArrowDown size={14} aria-hidden="true" /></button>
                  {props.canDelete(option) ? <button type="button" className="quotation-exclusion-control quotation-exclusion-delete"
                    aria-label={`Delete reusable exclusion ${label}`} title="Delete reusable exclusion" disabled={Boolean(props.deletingLabel)} onClick={() => props.onDelete(option)}><Trash2 size={14} aria-hidden="true" /></button> : null}
                </div>
              </div>;
            })}
          </div>
        </section>;
      })}
    </div>
  </>;
}
